import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adminSocketService } from '../../services/adminSocketService';
import {
  addMessageToAction,
  setSendingMessage,
  setSendingMessageError,
  updateConversationLatestMessageAction
} from '../../store/Actions/AdminChatActions';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { getProducts } from '../../store/AsyncMethods/ProductMethod';
import { getOrdersByUser } from '../../store/AsyncMethods/OrdersMethod';
const clsx = (...classes) => classes.filter(Boolean).join(' '); 


const staticOrders = [
  { id: 'ORD-987', status: 'Shipped', total: '249.98', date: '2023-10-25', itemCount: 2, customer: 'John Doe', items: ['Premium Leather Wallet', 'Classic Chrono Watch'], tracking: 'TRK123456789' },
  { id: 'ORD-988', status: 'Processing', total: '49.99', date: '2023-10-27', itemCount: 1, customer: 'Jane Smith', items: ['Modern Bluetooth Speaker'], tracking: 'TRK987654321' },
];
const staticVouchers = [
  { code: 'WINTERSALE20', discount: '20% Off All Items', expiry: '2024-01-31', minPurchase: 5000, maxDiscount: 1000, usage: 'Unlimited', category: 'Seasonal' },
  { code: 'FREESHIP', discount: 'Free Shipping', expiry: '2023-12-31', minPurchase: 5000, maxDiscount: 150, usage: 'Once per customer', category: 'Shipping' },
];
const ProductCard = ({ product }) => (
  <div className="p-3 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
    <div className="flex items-start gap-3">
      <div className="w-16 h-16 bg-slate-100 rounded-md flex-shrink-0">
        {product.all_images && product.all_images.length > 0 ? (
          <img 
            src={product.image_path + product.all_images[0].image} 
            alt={product.title} 
            className="w-full h-full object-cover rounded-md"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="pi pi-box text-2xl text-slate-400"></i>
          </div>
        )}
      </div>
      <div className="flex-grow">
        <p className="font-semibold text-sm text-slate-800 line-clamp-1">{product.title}</p>
        <p className="text-xs text-slate-500">ID: {product.id} | Article: {product.article_name}</p>
        
        {/* Variants Section */}
        <div className="mt-2 space-y-2">
          {product.varients?.map((variant, index) => (
            <div key={variant.varient_id} className="bg-slate-50 p-2 rounded-md">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-medium text-slate-700">
                    Variant {index + 1}
                    {variant.properties?.map(prop => (
                      <span key={prop.name} className="ml-2 text-slate-500">
                        {prop.name}: {prop.value}
                      </span>
                    ))}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs font-medium text-emerald-600">Rs{variant.price}</span>
                    {variant.discount > 0 && (
                      <span className="text-xs text-slate-500 line-through">Rs{variant.purchasePrice}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Tag 
                    value={variant.quantity > 0 ? 'In Stock' : 'Out of Stock'} 
                    severity={variant.quantity > 0 ? 'success' : 'danger'} 
                  />
                  <span className="text-xs text-slate-500">Qty: {variant.quantity}</span>
                </div>
              </div>
              {variant.shipment_charges > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Shipping: Rs{variant.shipment_charges}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Category Info */}
        <div className="mt-2 flex items-center gap-2">
          <Tag value={product.category_name} severity="info" />
          {product.sub_category_name && (
            <Tag value={product.sub_category_name} severity="info" />
          )}
        </div>
      </div>
    </div>
  </div>
);
const OrderCard = ({ order }) => (
  <div className="p-3 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start gap-4">
      <div className="flex-grow">
        <p className="font-semibold text-sm text-slate-800">Order #{order.id}</p>
        <p className="text-xs text-slate-500">Customer: {order.user?.name}</p>
        <div className="mt-2">
          <p className="text-xs text-slate-600">Items:</p>
          <div className="space-y-2 mt-1">
            {order.products?.map((product, index) => (
              <div key={index} className="flex items-center gap-2 bg-slate-50 p-2 rounded-md">
                <div className="w-12 h-12 bg-slate-100 rounded-md flex-shrink-0">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.title}
                      className="w-full h-full object-cover rounded-md"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="pi pi-image text-slate-400"></i>
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <p className="text-xs font-medium text-slate-800">{product.title}</p>
                  <p className="text-xs text-slate-500">
                    Quantity: {product.quantity} × Rs{product.price_at_purchase}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <Tag 
          value={order.status} 
          severity={
            order.status === 'shipping' ? 'info' : 
            order.status === 'hand_over' ? 'success' :
            order.status === 'pending' ? 'warning' : 'danger'
          } 
        />
        <p className="text-sm font-bold text-slate-800 mt-1">Rs{order.total_amount}</p>
        <p className="text-xs text-slate-500 mt-1">
          {order.courier_company ? `Courier: ${order.courier_company}` : 'No courier assigned'}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {order.tracking_id ? `Tracking: ${order.tracking_id}` : 'No tracking ID'}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {new Date(order.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  </div>
);
const VoucherCard = ({ voucher }) => (
  <div className="p-3 bg-white border border-dashed border-indigo-400 rounded-lg hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start gap-4">
      <div>
        <p className="font-bold text-sm text-indigo-600 tracking-wider">{voucher.code}</p>
        <p className="text-sm text-slate-700 mt-1">{voucher.discount}</p>
        <div className="mt-2 space-y-1">
          <p className="text-xs text-slate-500">Min Purchase: Rs{voucher.minPurchase.toLocaleString()}</p>
          <p className="text-xs text-slate-500">Max Discount: Rs{voucher.maxDiscount.toLocaleString()}</p>
          <p className="text-xs text-slate-500">Usage: {voucher.usage}</p>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <Tag value={voucher.category} severity="info" />
        <p className="text-xs text-slate-500 mt-2">Expires: {new Date(voucher.expiry).toLocaleDateString()}</p>
      </div>
    </div>
  </div>
);

const ChatHeader = ({ userId }) => (
  <header className="p-4 border-b border-slate-200 sticky top-0 bg-white/80 backdrop-blur-sm z-20 flex-shrink-0">
    <h2 className="text-lg font-semibold text-slate-800">Chat with User: {userId}</h2>
  </header>
);

const MessageBubble = ({ msg }) => { 
  const isAdmin = msg.is_admin === 1;
  const renderPayload = () => {
    if (!msg.payload) return null;
    switch (msg.payload.type) {
      case 'product': return <ProductCard product={msg.payload.content} />;
      case 'order': return <OrderCard order={msg.payload.content} />;
      case 'voucher': return <VoucherCard voucher={msg.payload.content} />;
      default: return null;
    }
  }
  return (
    <div className={clsx('flex', isAdmin ? 'justify-end' : 'justify-start')}>
      <div className={clsx('max-w-[80%] lg:max-w-[60%] flex flex-col', isAdmin ? 'items-end' : 'items-start')}>
        <div className={clsx(
          'relative px-4 py-3 shadow-sm rounded-2xl',
          isAdmin 
            ? 'bg-blue-500 text-white rounded-br-lg' 
            : 'bg-white text-slate-800 border border-slate-200 rounded-bl-lg'
        )}>
          {msg.message && (
            <p className={clsx(
              'text-sm whitespace-pre-wrap break-words',
              isAdmin ? 'text-white' : 'text-slate-800'
            )}>
              {msg.message}
            </p>
          )}
          {msg.payload && (
            <div className={clsx(
              'mt-2 rounded-lg overflow-hidden',
              isAdmin ? 'bg-white/10' : 'bg-slate-50'
            )}>
              {renderPayload()}
            </div>
          )}
        </div>
        <p className={clsx("text-xs mt-1 px-2", isAdmin ? 'text-slate-400' : 'text-slate-500')}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
};

const MessageList = ({ messages, loading }) => {
    const scrollRef = useRef(null);
    useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
    if (loading) { return <div className="p-4 space-y-4"><Skeleton height="4rem" width="60%" /><Skeleton height="5rem" width="50%" className="ml-auto !mr-0" /></div>; }
    return <div className="flex-1 p-4 space-y-6 overflow-y-auto bg-slate-100">{messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}<div ref={scrollRef} /></div>;
};

const ToolPanel = ({ toolType, onSendPayload }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const { products } = useSelector((state) => state.ProductReducer);
    const { allOrders } = useSelector((state) => state.OrderReducer);
    
    const items = {
        products: products || [],
        orders: allOrders || [],
        vouchers: staticVouchers,
    };
    
    const filteredItems = items[toolType].filter(item => {
        if (toolType === 'products') {
            const searchLower = searchQuery.toLowerCase();
            return item.title?.toLowerCase().includes(searchLower) ||
                   item.id?.toString().includes(searchLower) ||
                   item.article_name?.toLowerCase().includes(searchLower) ||
                   item.category_name?.toLowerCase().includes(searchLower) ||
                   item.varients?.some(v => 
                     v.properties?.some(p => 
                       p.value.toLowerCase().includes(searchLower)
                     )
                   );
        }
        if (toolType === 'orders') {
            return item.id?.toString().includes(searchQuery) ||
                   item.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   item.products?.some(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return (item.title || item.id || item.code).toLowerCase().includes(searchQuery.toLowerCase());
    });

    const renderItem = (item) => (
        <div className="py-2 px-1 flex items-center gap-2 hover:bg-slate-50 rounded-md cursor-pointer transition-colors">
            <div className="flex-grow">
                {toolType === 'products' && <ProductCard product={item} />}
                {toolType === 'orders' && <OrderCard order={item} />}
                {toolType === 'vouchers' && <VoucherCard voucher={item} />}
            </div>
            <Button 
                icon="pi pi-send" 
                rounded 
                text 
                severity="secondary" 
                tooltip="Send Item"
                className="hover:bg-slate-200"
                onClick={() => {
                    if (toolType === 'products') {
                        // For products, include the first variant's ID
                        const variantId = item.varients?.[0]?.varient_id;
                        onSendPayload(
                            { 
                                type: toolType.slice(0, -1), 
                                content: {
                                    ...item,
                                    selected_variant_id: variantId
                                }
                            }, 
                            `Sent ${toolType.slice(0, -1)}: ${item.title} (Variant ID: ${variantId})`
                        );
                    } else {
                        onSendPayload(
                            { type: toolType.slice(0, -1), content: item }, 
                            `Sent ${toolType.slice(0, -1)}: ${item.id}`
                        );
                    }
                }}
            />
        </div>
    );
    
    return (
        <div className="border-b border-slate-200 bg-white transition-all duration-300">
            <div className="p-3 border-b border-slate-200">
                <span className="p-input-icon-left w-full">
                    <i className="pi pi-search text-slate-400" />
                    <InputText 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search ${toolType}...`} 
                        className="w-full !pl-10"
                    />
                </span>
            </div>
            <div className="max-h-64 overflow-y-auto px-4 pb-2">
                {filteredItems.length > 0 ? (
                    filteredItems.map((item, i) => <div key={i}>{renderItem(item)}</div>)
                ) : (
                    <div className="text-center p-4">
                        <i className="pi pi-search text-2xl text-slate-300 mb-2"></i>
                        <p className="text-slate-500">No {toolType} found matching "{searchQuery}"</p>
                    </div>
                )}
            </div>
        </div>
    );
}

const ChatInputArea = ({ onSendText, onSendPayload, disabled, activeConversationUserId }) => {
    const [newMessage, setNewMessage] = useState("");
    const [activeTool, setActiveTool] = useState('message');

    const handleSendText = () => {
        if (!newMessage.trim()) return;
        onSendText(newMessage.trim());
        setNewMessage("");
        if (activeConversationUserId) {
          adminSocketService.sendTypingStatus({ user_id: activeConversationUserId, isTyping: false });
        }
    };

    const handleSendPayload = (payload, text) => {
        onSendPayload(payload, text);
        setActiveTool('message'); 
    };

    const handleTyping = (e) => {
      setNewMessage(e.target.value);
      if (activeConversationUserId) {
        adminSocketService.sendTypingStatus({ user_id: activeConversationUserId, isTyping: true });
      }
    };

    const handleBlur = () => {
      if (activeConversationUserId) {
        adminSocketService.sendTypingStatus({ user_id: activeConversationUserId, isTyping: false });
      }
    };

    const tabs = [
      { id: 'message',  icon: 'pi-comment',        label: 'Message'  },
      { id: 'products', icon: 'pi-box',            label: 'Products' },
      { id: 'orders',   icon: 'pi-shopping-cart',  label: 'Orders'   },
      { id: 'vouchers', icon: 'pi-ticket',         label: 'Vouchers' },
    ];
    
    return (
        <div className="flex flex-col border-t border-slate-200 bg-white">
            {activeTool !== 'message' && 
                <ToolPanel toolType={activeTool} onSendPayload={handleSendPayload} />
            }
            
            <div className="flex border-b border-slate-200 bg-slate-50">
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTool(tab.id)}
                        className={clsx(
                            'flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2',
                            activeTool === tab.id 
                                ? 'bg-white text-blue-600 border-t-2 border-blue-500' 
                                : 'text-slate-500 hover:bg-slate-200/50'
                        )}
                    >
                       <i className={clsx('pi', tab.icon)} />
                       <span>{tab.label}</span>
                    </button>
                ))}
            </div>
            
            <div className="p-4 bg-white flex items-center gap-3">
                <textarea
                    value={newMessage}
                    onChange={handleTyping}
                    onBlur={handleBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendText(); } }}
                    placeholder="Type your message..."
                    className="flex-grow p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-shadow"
                    rows={2}
                    disabled={disabled}
                />
                <Button icon="pi pi-send" aria-label="Send" onClick={handleSendText} disabled={disabled || !newMessage.trim()} />
            </div>
        </div>
    );
};

const AdminChatView = () => {
  const dispatch = useDispatch();
const { products } = useSelector((state) => state.ProductReducer);
const { allOrders } = useSelector((state) => state.OrderReducer);
const {
  activeConversationUserId,
  activeConversationMessages,
  loadingActiveConversation,
  sendingAdminMessage: sendingMessage,
} = useSelector((state) => state.AdminChatReducer);
useEffect(() => {
  dispatch(getProducts());
  dispatch(getOrdersByUser(activeConversationUserId));
}, []);
useEffect(() => {
  if (activeConversationUserId) {
    dispatch(getOrdersByUser(activeConversationUserId));
  }
}, [activeConversationUserId]);

useEffect(() => {
  console.log("products", products);
}, [products]);
useEffect(() => {
  console.log("orders", allOrders);
}, [allOrders]);


  const handleSendPayload = (payload, fallbackText) => {
    if (!activeConversationUserId || sendingMessage) return;
    const fullPayloadToServer = { user_id: activeConversationUserId, message: fallbackText, payload };

    dispatch(setSendingMessage(true));
    dispatch(setSendingMessageError(null));

    adminSocketService.sendMessage(fullPayloadToServer, (response) => {
      dispatch(setSendingMessage(false));
      if (response.status === 'ok') {
        dispatch(addMessageToAction(response.sentMessage));
        dispatch(updateConversationLatestMessageAction({ message: response.sentMessage, activeUserId: activeConversationUserId }));
      } else {
        dispatch(setSendingMessageError(response.msg));
      }
    });
  };

  const handleSendTextMessage = (message) => {
    handleSendPayload({ type: 'text', content: message }, message);
  };

  if (!activeConversationUserId) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-50 text-slate-500">
        <i className="pi pi-comments" style={{ fontSize: '3rem' }}></i>
        <h3 className="mt-4 text-xl font-medium">No Conversation Selected</h3>
        <p className="mt-1 text-sm">Please select a conversation from the list to start chatting.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-white overflow-hidden">
      <ChatHeader userId={activeConversationUserId} />
      
      <main className="flex-1 flex flex-col min-h-0">
         <MessageList
          messages={activeConversationMessages}
          loading={loadingActiveConversation}
        />
        
        <ChatInputArea
          onSendText={handleSendTextMessage}
          onSendPayload={handleSendPayload}
          disabled={sendingMessage}
          activeConversationUserId={activeConversationUserId}
        />
      </main>
    </div>
  );
};

export default AdminChatView;